import { useEffect, useMemo, useState } from 'react'
import { Circle } from '@/types'
import { useGetGroupMembersByID } from '@/hooks/api/dashboard'

/**
 * Maps each connection's userID to the (system) circle name(s) they've been
 * assigned to, by fetching every system circle's member list once. Backs the
 * "All circles / Family / Friends / …" filter row and the per-row circle
 * badge on the Connections tab with real data (there's no per-connection
 * circle field on `MyConnection` itself — only the aggregate membership
 * lists from `circleConnList`).
 */
export function useConnectionCircles(systemCircles: Circle[]) {
  const getGroupMembers = useGetGroupMembersByID()
  const [byUser, setByUser] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(false)

  const circleIDs = useMemo(() => systemCircles.map((c) => c.circleID).join(','), [systemCircles])

  useEffect(() => {
    if (!circleIDs) {
      setByUser({})
      return
    }
    let cancelled = false
    setLoading(true)
    Promise.allSettled(systemCircles.map((circle) => getGroupMembers.mutateAsync(circle.circleID).then((members) => ({ circle, members }))))
      .then((results) => {
        if (cancelled) return
        const map: Record<string, string[]> = {}
        results.forEach((result) => {
          if (result.status !== 'fulfilled') return
          const { circle, members } = result.value
          ;(members || []).forEach((member) => {
            if (!map[member.userID]) map[member.userID] = []
            map[member.userID].push(circle.circleName)
          })
        })
        setByUser(map)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circleIDs])

  return { circlesByUser: byUser, loading }
}
